/**
 * Baby Development Milestones Page
 * Manage milestones for a phase with drag-drop sorting
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, GripVertical, Pencil, Trash2, Check, X, Baby } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Toggle } from '@/components/ui/Toggle';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import type { BabyDevelopmentPhase, BabyDevelopmentMilestone } from './types';

export function BabyDevelopmentMilestonesPage() {
  const supabase = getSupabaseAdminClient();
  const { id: phaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Fetch phase info
  const { data: phase } = useQuery({
    queryKey: ['baby-development-phase', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_phases')
        .select('*')
        .eq('id', phaseId)
        .eq('locale', contentLocale)
        .single();
      if (error) throw error;
      return data as BabyDevelopmentPhase;
    },
    enabled: !!phaseId,
  });

  // Fetch milestones
  const { data: milestones, isLoading } = useQuery({
    queryKey: ['baby-development-milestones', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_milestones')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('locale', contentLocale)
        .order('sort_order');
      if (error) throw error;
      return data as BabyDevelopmentMilestone[];
    },
    enabled: !!phaseId,
  });

  // Add milestone
  const addMutation = useMutation({
    mutationFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const maxOrder = milestones?.reduce((max, m) => Math.max(max, m.sort_order), 0) ?? 0;
      const { error } = await (supabase as any)
        .from('baby_development_milestones')
        .insert({
          phase_id: phaseId,
          title: newTitle,
          description: newDescription || null,
          sort_order: maxOrder + 1,
          locale: contentLocale,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Meilenstein hinzugefügt');
      queryClient.invalidateQueries({ queryKey: ['baby-development-milestones', phaseId, contentLocale] });
      setIsAdding(false);
      setNewTitle('');
      setNewDescription('');
    },
    onError: (error) => {
      toast.error('Fehler: ' + (error as Error).message);
    },
  });

  // Update milestone
  const updateMutation = useMutation({
    mutationFn: async ({ milestoneId, title, description }: { milestoneId: string; title: string; description: string }) => {
      const { error } = await (supabase as any)
        .from('baby_development_milestones')
        .update({ title, description: description || null })
        .eq('id', milestoneId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Meilenstein aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['baby-development-milestones', phaseId, contentLocale] });
      setEditingId(null);
    },
    onError: (error) => {
      toast.error('Fehler: ' + (error as Error).message);
    },
  });

  // Toggle active
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ milestoneId, isActive }: { milestoneId: string; isActive: boolean }) => {
      const { error } = await (supabase as any)
        .from('baby_development_milestones')
        .update({ is_active: isActive })
        .eq('id', milestoneId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-development-milestones', phaseId, contentLocale] });
    },
  });

  // Delete milestone
  const deleteMutation = useMutation({
    mutationFn: async (milestoneId: string) => {
      const { error } = await (supabase as any)
        .from('baby_development_milestones')
        .delete()
        .eq('id', milestoneId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Meilenstein gelöscht');
      queryClient.invalidateQueries({ queryKey: ['baby-development-milestones', phaseId, contentLocale] });
    },
    onError: (error) => {
      toast.error('Fehler: ' + (error as Error).message);
    },
  });

  // Move milestone (reorder)
  const moveMutation = useMutation({
    mutationFn: async ({ milestoneId, direction }: { milestoneId: string; direction: 'up' | 'down' }) => {
      if (!milestones) return;

      const currentIndex = milestones.findIndex(m => m.id === milestoneId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex < 0 || targetIndex >= milestones.length) return;

      const current = milestones[currentIndex];
      const target = milestones[targetIndex];

      // Swap sort_order values
      await (supabase as any)
        .from('baby_development_milestones')
        .update({ sort_order: target.sort_order })
        .eq('id', current.id);

      await (supabase as any)
        .from('baby_development_milestones')
        .update({ sort_order: current.sort_order })
        .eq('id', target.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-development-milestones', phaseId, contentLocale] });
    },
  });

  const startEditing = (milestone: BabyDevelopmentMilestone) => {
    setEditingId(milestone.id);
    setEditTitle(milestone.title);
    setEditDescription(milestone.description || '');
  };

  // Early return after all hooks
  if (!phaseId) {
    return <div className="text-center py-12 text-slate-500">Phase nicht gefunden</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/features/baby-development')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
            <Baby className="w-6 h-6 text-teal-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Meilensteine: {phase?.display_range ?? ''}</h1>
            <p className="text-slate-500">Phase {phase?.phase_number}: "{phase?.subtitle}"</p>
          </div>
          <LocaleSelector />
        </div>
      </div>

      {/* Add Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Meilenstein hinzufügen
        </button>
      </div>

      {/* Add Form */}
      {isAdding && (
        <div className="bg-white rounded-lg shadow-sm p-4 border-2 border-teal-200">
          <div className="space-y-4">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Titel des Meilensteins"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              autoFocus
            />
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Beschreibung (optional)"
              rows={2}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewTitle('');
                  setNewDescription('');
                }}
                className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Abbrechen
              </button>
              <button
                onClick={() => addMutation.mutate()}
                disabled={!newTitle.trim() || addMutation.isPending}
                className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Milestones List */}
      <div className="bg-white rounded-lg shadow-sm divide-y">
        {milestones?.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Noch keine Meilensteine vorhanden
          </div>
        )}

        {milestones?.map((milestone, index) => (
          <div
            key={milestone.id}
            className={`p-4 ${!milestone.is_active ? 'bg-slate-50' : ''}`}
          >
            {editingId === milestone.id ? (
              // Edit mode
              <div className="space-y-3">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  autoFocus
                />
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Beschreibung (optional)"
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => updateMutation.mutate({
                      milestoneId: milestone.id,
                      title: editTitle,
                      description: editDescription,
                    })}
                    disabled={!editTitle.trim()}
                    className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    <Check className="w-4 h-4" />
                    Speichern
                  </button>
                </div>
              </div>
            ) : (
              // Display mode
              <div className="flex items-center gap-4">
                {/* Drag Handle / Order Controls */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveMutation.mutate({ milestoneId: milestone.id, direction: 'up' })}
                    disabled={index === 0}
                    className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4 text-slate-400 rotate-90" />
                  </button>
                  <button
                    onClick={() => moveMutation.mutate({ milestoneId: milestone.id, direction: 'down' })}
                    disabled={index === (milestones?.length ?? 0) - 1}
                    className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4 text-slate-400 -rotate-90" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className={`font-medium ${!milestone.is_active ? 'text-slate-400' : 'text-slate-900'}`}>
                    {milestone.title}
                  </h3>
                  {milestone.description && (
                    <p className="text-sm text-slate-500 mt-1">{milestone.description}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={milestone.is_active}
                    onChange={(checked) => toggleActiveMutation.mutate({
                      milestoneId: milestone.id,
                      isActive: checked,
                    })}
                    label="Aktiv"
                  />
                  <button
                    onClick={() => startEditing(milestone)}
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Meilenstein wirklich löschen?')) {
                        deleteMutation.mutate(milestone.id);
                      }
                    }}
                    className="p-2 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
